import { useAtom, useAtomValue } from 'jotai';
import { FlaskConical } from 'lucide-react';

import { DialogTitleHeader } from '@/components/molecules/dialog-title-header';
import { focusDialogStart } from '@/utils/focus-dialog-start';
import { Button } from '@/ux-common/components/button/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterButtons,
} from '@/ux-common/components/dialog/dialog';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

import { labsAssignmentAtom, labsDialogOpenAtom } from './labs.state';
import { optIntoPrototype, optOutOfPrototype } from './prototype-assignment';
import { prototypeDescription, prototypeTitle } from './prototype-flag';

export const LABS_INTRO =
  "CrossnoKaye Labs gives you early access to features we're still building. What you tell us " +
  'about them shapes what ships to everyone.';

/**
 * Explains CrossnoKaye Labs and the prototype on offer, and lets the user leave or try it.
 * Mount once (in AppLayout); opened from the profile menu via labsDialogOpenAtom.
 */
export const LabsDialog = () => {
  const { status, prototype } = useAtomValue(labsAssignmentAtom);
  const [open, setOpen] = useAtom(labsDialogOpenAtom);

  const optedIn = status === 'opted-in';

  const handleClick = () => {
    setOpen(false);
    if (optedIn) {
      optOutOfPrototype();
    } else {
      optIntoPrototype(prototype.branch);
    }
  };

  return (
    <Dialog open={open && status !== 'none'} onOpenChange={setOpen}>
      <DialogContent
        data-component="LabsDialog"
        className="dialog-md"
        onOpenAutoFocus={focusDialogStart}
      >
        <DialogTitleHeader
          title={
            <span className="flex items-center gap-2">
              <FlaskConical className="glyph glyph-md" aria-hidden="true" />
              {'CrossnoKaye Labs'}
            </span>
          }
        />
        <DialogBody>
          <Stack className="stack-sm">
            <DialogDescription asChild>
              <Typography className="text-body-base">{LABS_INTRO}</Typography>
            </DialogDescription>
            <div className="flex flex-col gap-1 rounded-lg border border-border p-4">
              <div className="text-xs font-semibold text-muted-foreground">
                {optedIn ? 'Prototype active' : 'Prototype available'}
              </div>
              <div className="text-base font-semibold">{prototypeTitle(prototype)}</div>
              <p className="text-sm text-muted-foreground">{prototypeDescription(prototype)}</p>
            </div>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogFooterButtons>
            <Button className="btn-tier2" onClick={handleClick}>
              {optedIn ? 'Leave prototype' : 'Try it'}
            </Button>
          </DialogFooterButtons>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
